/**
 * The one predicate deciding whether a firm's PMS publishes a contact-type catalogue at all.
 *
 * Pure and separately tested, the same split as `contact-binding-state.ts`: the rule is subtle
 * enough that it is far cheaper to pin down here than through a rendered card.
 */

import type { PmsContactTypeCatalogue } from './types';

/**
 * Does this firm's PMS publish a per-firm contact-type catalogue?
 *
 * **This is deliberately NOT `contactTypes.length > 0`.** An empty list conflates two facts the
 * backend keeps apart, and only one of them is a reason to withhold the manual refresh:
 *
 * | `syncedAt` | `contactTypes` | Meaning | Offer a refresh? |
 * |---|---|---|---|
 * | `null` | `[]` | nobody has asked the provider yet | **yes** — this is what it is for |
 * | set | `[]` | asked; this provider publishes none (Smokeball) | no |
 * | set | non-empty | asked; here they are | yes |
 *
 * The first row is the case that makes the naive test self-defeating. An integration enabled before
 * the catalogue store existed holds nothing until the nightly sweep runs, and the manual refresh is
 * precisely the remedy for that wait — so gating it on a non-empty list would hide the button in the
 * one situation it was built for.
 *
 * Note what this is **not**: a provider-name check. Smokeball is excluded because it answers the
 * question with an empty catalogue, not because it is named here — a third provider that publishes
 * one is offered the action with no change to this file.
 *
 * **Since VALE-587 this is also the picker's visibility gate**, because the refresh it governs moved
 * off the integration card and onto the picker itself. The narrower `contactTypes.length > 0` test
 * (`isAvailable` on `usePmsContactTypeSelection`) survives, and still decides whether there is a
 * *select row* to offer — a never-synced catalogue genuinely has nothing to pick from. The two now
 * mean: this one, "should this surface exist at all?"; that one, "is there anything in it to choose?"
 */
export function publishesContactTypeCatalogue(
  catalogue: PmsContactTypeCatalogue | undefined
): boolean {
  if (!catalogue) return false;

  return catalogue.syncedAt === null || catalogue.contactTypes.length > 0;
}
